/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions,react/display-name,unused-imports/no-unused-vars */
import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  createRef,
  forwardRef,
  createElement,
} from 'react';
import {
  number, string, oneOfType, oneOf, shape, func, bool, arrayOf,
} from 'prop-types';
import uniqueId from 'lodash/uniqueId';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import {
  equals, isNil, evolve, flip, reduce, when, is, over, lensProp, defaultTo,
} from 'ramda';
import classNames from 'classnames';
import styleVariables from 'styles/variables';
import { addEventListener } from 'consolidated-events';
import {
  Small,
  ReadOnlyField,
  Icon,
  Select,
  DateInput,
  Checkbox,
  SegmentedControl,
  RadioControl,
  Switch,
} from 'components/common';
import ApplicationFormSummary from 'components/common/applications/ApplicationFormSummary';
import { sanitize, setSelectionOffset } from 'helpers/Utils';
import { startAnimation } from './helpers/animation';
import DialogBox from './DialogBox';
import OutsideClickHandler from './OutsideClickHandler';

import './Input.scss';

export const inputFieldDefault = ({
  inputRefs = {},
  field,
  success,
  error,
  stateField,
  isClean,
  behaviors,
  validateField,
  changeHandler,
  blurHandler,
  keyPressHandler,
  paramOptions,
  keyUpHandler,
  id,
  hideAsterisk,
  dataSelectorId,
  hideSuccessIcon,
  showModal,
} = {}) => {
  let fieldLabelToShow;
  if (field.required && !hideAsterisk && field.label) {
    fieldLabelToShow = `${field.label}*`;
  } else if (field.label) {
    fieldLabelToShow = `${field.label}`;
  }

  const options = paramOptions || {};

  if (field.type === 'readOnly') {
    return (
      <ReadOnlyField
        title={fieldLabelToShow}
        alignItems="flex-start"
        description={field.description?.position ? (
          {
            position: field.description?.position,
            render: () => field.description && (
              <ApplicationFormSummary
                summarySection={{ valueFormatter: field.description.valueFormatter }}
                showModal={showModal}
              />
            ),
          }) : field.description
        }
        descriptionShowAsBlock={field.description?.showAs === 'block'}
        showExternalValue={field.subtype === 'showExternalValue'}
        dynamicValue={field.dynamicValue}
        value={field.value}
        showAsColumns={field.description?.showAsColumns}
      />
    );
  }

  if (field.type === 'checkbox') {
    return (
      <Checkbox
        labelHtml={fieldLabelToShow}
        alignItems="flex-start"
        isClean={isClean}
        success={success}
        error={error}
        validationText={field.message}
        value={field.value}
        selected={!!field.value}
        onChange={changeHandler}
        dataSelectorId={dataSelectorId}
      />
    );
  }

  if (field.type === 'switch') {
    return (
      <Switch
        selected={field.selected || !!field.value}
        disabled={field.disabled}
        onChange={changeHandler}
        value={field.value}
        id={fieldLabelToShow}
        booleanValuesOnly={field.booleanValuesOnly}
        type={field.subtype}
      >
        {fieldLabelToShow}
      </Switch>
    );
  }

  if (field.type === 'segment') {
    return (
      <SegmentedControl
        title={fieldLabelToShow}
        options={field.options}
        success={success}
        configClasses={field?.customColumnClassName}
        error={error}
        validationText={field.message}
        isClean={isClean}
        value={field.value}
        massive={!!field.options[0] && !!field.options[0].icon}
        onChange={changeHandler}
        dataSelectorId={dataSelectorId}
        description={field?.description?.position ? (
          {
            position: field?.description?.position,
            render: () => field?.description && (
              <ApplicationFormSummary
                summarySection={{ valueFormatter: field.description.valueFormatter }}
                showModal={showModal}
              />
            ),
          }) : field.description?.value
        }
      />
    );
  }

  if (field.type === 'radio') {
    return (
      <RadioControl
        title={fieldLabelToShow}
        options={field.options}
        success={success}
        error={error}
        validationText={field.message}
        isClean={isClean}
        value={field.value}
        onChange={changeHandler}
        idPrefix={field.optionsKey}
        inline={field.subtype === 'inline'}
        dataSelectorId={dataSelectorId}
      />
    );
  }

  function mapAs(type) {
    if (['textarea'].includes(type)) {
      return 'textarea';
    }
    if (['select'].includes(type)) {
      return 'select';
    }
    return 'input';
  }

  // in case the are fields with identical labels
  const inputRef = field?.labelRefId || field?.label;
  // eslint-disable-next-line
  inputRefs[inputRef] = inputRefs[inputRef] || createRef();
  return (
    <Input
      ref={inputRefs[inputRef]}
      id={id}
      dataSelectorId={dataSelectorId}
      disabled={field.disabled || field?.inputProps?.disabled}
      title={fieldLabelToShow}
      required={field.required}
      description={field?.description?.position ? (
        {
          position: field?.description?.position,
          render: () => field?.description && (
            <ApplicationFormSummary
              summarySection={{ valueFormatter: field.description.valueFormatter }}
              showModal={showModal}
            />
          ),
        }) : field.description?.value
      }
      placeholder={field.placeholder}
      type={field.type}
      as={mapAs(field.type)}
      options={field.options}
      value={stateField?.value}
      behaviors={behaviors}
      validate={validateField}
      onChange={value => changeHandler({ target: { value } })}
      onBlur={blurHandler ? (value => blurHandler({ target: { value } }))
        : (value => changeHandler({ target: { value } }))}
      onKeyPress={keyPressHandler}
      onKeyUp={keyUpHandler}
      rightText={options.rightText}
      leftText={options.leftText}
      leftBoxText={options.leftBoxText}
      showSuccessIcon={!hideSuccessIcon}
      inputProps={field.inputProps}
      dateInputProps={field.dateInputProps}
      validationText={field.message}
    />
  );
};

inputFieldDefault.propTypes = {
  inputRefs: string,
  field: shape({
    label: string,
    success: bool,
  }),
  success: bool,
  error: bool,
  stateField: shape({}),
  isClean: bool,
  behaviors: oneOfType([
    shape({
      coerce: func,
      parse: func,
      normalize: func,
      finalize: func,
      format: func,
    }),
    arrayOf(shape({
      coerce: func,
      parse: func,
      normalize: func,
      finalize: func,
      format: func,
    })),
  ]),
  validateField: func,
  changeHandler: func,
  blurHandler: func,
  keyPressHandler: func,
  paramOptions: shape({}),
  keyUpHandler: func,
  id: string,
  hideAsterisk: bool,
  dataSelectorId: string,
  hideSuccessIcon: bool,
  showModal: bool,
};

export const inputFieldAs = ({
  inputRefs = {},
  field,
  stateField,
  behaviors,
  validateField,
  changeHandler,
  keyPressHandler,
  paramOptions,
  keyUpHandler,
  dataSelectorId,
  showModal,
} = {}) => {
  let fieldLabelToShow = '';
  if (field.required) {
    fieldLabelToShow = `${field.label}*`;
  } else {
    fieldLabelToShow = `${field.label}`;
  }

  const options = paramOptions || {};

  // eslint-disable-next-line
  inputRefs[field.label] = inputRefs[field.label] || createRef();

  return (
    <Input
      ref={inputRefs[field.label]}
      name={field.label}
      title={fieldLabelToShow}
      required={field.required}
      description={{
        position: field?.description?.position,
        render: () => field?.description && (
          <ApplicationFormSummary
            summarySection={{ valueFormatter: field.description.valueFormatter }}
            showModal={showModal}
          />
        ),
      }}
      placeholder={field.placeholder}
      as={field.type}
      options={field.options}
      behaviors={behaviors}
      value={stateField?.value}
      validate={validateField}
      onChange={(value) => {
        changeHandler(
          field.type === 'select' ? find(field.options, { value }) : { target: { value } }
        );
      }}
      onBlur={(value) => {
        changeHandler(
          field.type === 'select' ? find(field.options, { value }) : { target: { value } }
        );
      }}
      onKeyPress={keyPressHandler}
      onKeyUp={keyUpHandler}
      rightText={options.rightText}
      leftText={options.leftText}
      dataSelectorId={dataSelectorId}
      inputProps={field.inputProps || { autoComplete: field.autoComplete }}
      dateInputProps={field.dateInputProps}
    />
  );
};

inputFieldAs.propTypes = {

  field: shape({
    required: bool,
    label: string,
    type: string,
    placeholder: string,
    description: shape({
      valueFormatter: string,
    }),
    options: shape({}),
    inputProps: shape({}),
    dateInputProps: shape({}),
    autoComplete: bool,
  }),
  inputRefs: string,
  stateField: shape({}),
  behaviors: oneOfType([
    shape({
      coerce: func,
      parse: func,
      normalize: func,
      finalize: func,
      format: func,
    }),
    arrayOf(shape({
      coerce: func,
      parse: func,
      normalize: func,
      finalize: func,
      format: func,
    })),
  ]),
  validateField: func,
  changeHandler: func,
  keyPressHandler: func,
  paramOptions: shape({}),
  keyUpHandler: func,
  dataSelectorId: string,
  showModal: bool,
};

const STATUS = {
  init: 0,
  dirty: 1,
  touched: 2,
};

const InputComponent = forwardRef(
  (
    {
      id,
      name,
      dataSelectorId,
      title,
      subtitle,
      description,
      leftIconName,
      leftText,
      leftBoxText,
      rightText,
      rightIconName,
      validationText,
      errorComponent: ErrorComponent,
      messageComponent: MessageComponent,
      defaultValue,
      value: controlledValue,
      /* PITFALL: DON'T DO THIS.
        Those functions need to bind their `this` context to `behavior` object
      */
      // behavior: {
      //   coerce, parse, normalize, finalize, format
      // },
      behaviors,
      formatDelay,
      validate,
      showFeedbackOnce,
      customPanel,
      showCustomPanel,
      onCustomPanelOutsideClick,
      overrideCustomPanelOutsideClick,
      isCustomPanelPadded,
      showSuccessIcon,
      message,
      onInputFieldIsVaild,
      suppressInvalidVisualResponse,
      displayMessageForTouchedState,
      ...props
    },
    ref
  ) => {
    /* Helpers */
    const applyLifecyles = (lifecycle, fallbackValue) => flip(reduce(
      (applied, behavior) => behavior?.[lifecycle]?.(applied, fallbackValue) ?? applied,
    ))(behaviors);

    /* Refs */
    const inputIdRef = useRef(id || uniqueId('input-'));
    const isModeControlledRef = useRef(
      (function() {
        const modeControlled = controlledValue !== undefined;
        const modeUncontrolled = defaultValue !== undefined;
        if (modeControlled && modeUncontrolled) {
          // eslint-disable-next-line
          console.error(`Warning: You shoud not specify both 'value' and 'defaultValue' at the same time.\n \
          To use controlled mode specify 'value' only,\n \
          To use uncontrolled mode you can optionally specify 'defaultValue'`);
        }
        return modeControlled;
      })()
    );
    const onceRef = useRef(STATUS.init);
    const hotNormalizedValueRef = useRef();

    /* State */
    const [meta, setMeta] = useState({
      pristine: true,
      dirty: false,
      valid: null,
      invalid: null,
      error: '',
      active: false,
      visited: false,
      touched: false,
      blur: false,
    });
    const [uncontrolledValue, setUncontrolledValue] = useState(() => (isModeControlledRef.current
      ? {}
      : {
        normalizedValue: defaultValue,
        formattedString: applyLifecyles('format')(defaultValue),
      }));
    const value = isModeControlledRef.current
      ? {
        normalizedValue: controlledValue,
        formattedString: applyLifecyles('format')(controlledValue),
      }
      : uncontrolledValue;
    const initialValueRef = useRef(value);
    const [isCustomPanelShown, setIsCustomPanelShown] = useState(showCustomPanel);

    /* Effects */
    const borderWrapperRef = useRef();
    const arrowRef = useRef();
    const customPanelRef = useRef();

    const updateWidth = () => {
      const { current: borderWrapperEl } = borderWrapperRef;
      const { current: arrowEl } = arrowRef;
      const { current: customPanelEl } = customPanelRef;
      if (!borderWrapperEl || (!customPanelEl && !arrowEl)) return;

      const { x } = borderWrapperEl.getBoundingClientRect();

      if (window.innerWidth < styleVariables.screenXsMax) {
        // mobile

        // Hack to set mobile custom panel
        if (customPanelEl) {
          customPanelEl.style.left = `${-1 * x}px`;
          customPanelEl.style.width = `${window.innerWidth}px`;
        }

        // Hack to make arrow always show in the middle of input
        if (arrowEl) {
          arrowEl.style.paddingLeft = `${x + borderWrapperEl.clientWidth / 2}px`;
        }
      } else {
        if (customPanelEl) {
          customPanelEl.style.left = null;
          customPanelEl.style.width = null;
        }

        // Hack to make arrow always show in the middle of input
        if (arrowEl) {
          arrowEl.style.paddingLeft = `${borderWrapperEl.clientWidth / 2}px`;
        }
      }
    };

    useEffect(() => {
      const removeResizeListener = addEventListener(window, 'resize', updateWidth);
      startAnimation(updateWidth);
      return removeResizeListener;
    }, []);

    useEffect(() => {
      updateWidth();
    });

    useEffect(
      () => {
        if (meta.valid && meta.blur && onInputFieldIsVaild) {
          onInputFieldIsVaild(meta.valid);
        }
      },
      [meta.valid, meta.blur]
    );

    useEffect(() => {
      const error = validate?.(value.normalizedValue);
      setMeta(prevMeta => ({
        ...prevMeta,
        valid: isNil(error?.length) ? null : !error,
        invalid: isNil(error?.length) ? null : !!error,
        error,
      }));
    }, [value.normalizedValue]);

    /* Expose */
    const valueRef = useRef(value);
    useEffect(() => {
      valueRef.current = value;
    });
    useImperativeHandle(ref, () => ({
      get value() {
        return valueRef.current.normalizedValue;
      },
      // TODO: Use form-level validation approach instead
      validate() {
        const error = validate?.(valueRef.current.normalizedValue);
        setMeta(prevMeta => ({
          ...prevMeta,
          valid: isNil(error?.length) ? null : !error,
          invalid: isNil(error?.length) ? null : !!error,
          error,
        }));
      },
      touch() {
        onceRef.current = STATUS.touched;
        setMeta(prevMeta => ({
          ...prevMeta,
          touched: true,
        }));
      },
      getCurrentState() {
        return meta;
      },
    }), [validate]); // TODO: Remove this dep when func validate is removed

    /* Handlers */
    const debouncedSetValue = useCallback(debounce(setUncontrolledValue, formatDelay), []);

    const handleFocus = () => {
      setMeta(prevMeta => ({
        ...prevMeta,
        active: true,
        visited: true,
      }));
      const { onFocus } = props;
      onFocus?.(value.normalizedValue);
    };

    /* thousandSeparated values handling */

    const inputEl = useRef();
    const [selectStart, setSelectionStart] = useState(0);
    const [selectEnd, setSelectionEnd] = useState(0);

    useEffect(
      () => {
        if (props.type === 'currency') inputEl.current?.setSelectionRange(selectStart, selectEnd);
      },
      [value.formattedString]
    );

    const handleChange = (eventOrValue) => {
      const inputString = eventOrValue?.target ? eventOrValue.target.value : eventOrValue;
      /* NOTE: Value lifecycle methods may return null to skip itself */
      // Coerce
      const newInputString = applyLifecyles('coerce', value.formattedString)(inputString);
      // Parse
      const newParsedValue = applyLifecyles('parse')(newInputString);
      // Normalize
      const newNormalizedValue = applyLifecyles('normalize', value.normalizedValue)(newParsedValue);
      // Format
      const newFormattedString = applyLifecyles('format')(newNormalizedValue);

      if (!isModeControlledRef.current) {
        setUncontrolledValue({
          normalizedValue: newNormalizedValue,
          formattedString: formatDelay > 0 ? newInputString : newFormattedString,
        });
        formatDelay > 0
          && debouncedSetValue(prevValue => ({
            ...prevValue,
            formattedString: newFormattedString,
          }));
      }

      onceRef.current = Math.max(onceRef.current, STATUS.dirty);

      if (eventOrValue?.target && props.type === 'currency') {
        const { target: { selectionStart, selectionEnd, value: eventValue } } = eventOrValue;

        setSelectionStart(selectionStart + setSelectionOffset(
          inputEl.current.defaultValue.length,
          eventValue.length,
          eventValue,
          selectionStart
        ));
        setSelectionEnd(selectionEnd + setSelectionOffset(
          inputEl.current.defaultValue.length,
          eventValue.length,
          eventValue,
          selectionStart
        ));
      }

      setMeta(prevMeta => ({
        ...prevMeta,
        pristine: equals(newNormalizedValue, initialValueRef.current.normalizedValue),
        dirty: !equals(newNormalizedValue, initialValueRef.current.normalizedValue),
      }));

      hotNormalizedValueRef.current = newNormalizedValue;

      const { onChange } = props;
      onChange?.(newNormalizedValue);
    };

    const handleBlur = () => {
      debouncedSetValue?.cancel();

      // Finalize
      const latestNormalizedValue = hotNormalizedValueRef.current || value.normalizedValue;
      hotNormalizedValueRef.current = null;
      const finalValue = applyLifecyles('finalize')(latestNormalizedValue);

      if (!isModeControlledRef.current) {
        setUncontrolledValue({
          normalizedValue: finalValue,
          formattedString: applyLifecyles('format')(finalValue),
        });
      }

      onceRef.current = Math.max(onceRef.current, STATUS.touched);

      setMeta(prevMeta => ({
        ...prevMeta,
        pristine: equals(finalValue, initialValueRef.current.normalizedValue),
        dirty: !equals(finalValue, initialValueRef.current.normalizedValue),
        active: false,
        touched: true,
        blur: true,
      }));

      const { onBlur } = props;
      onBlur?.(finalValue);
    };

    let visiblyValid = meta.valid && onceRef.current >= STATUS[showFeedbackOnce];
    let visiblyInvalid = !suppressInvalidVisualResponse
      && meta.invalid
      && onceRef.current >= STATUS[showFeedbackOnce];

    // Optional field won't show validation UI when empty
    if (value?.normalizedValue === '' && props?.required === false) {
      visiblyValid = false;
      visiblyInvalid = false;
    }

    const wrapperClasses = {
      'input-wrapper': true,
      error: visiblyInvalid,
      success: visiblyValid,
      disabled: props.disabled,
      focus: meta.active,
    };
    const displaySuccessIcon = ['select'].includes(props.as) ? false : showSuccessIcon;

    // TODO: Use render-prop technique
    const renderMainElement = () => {
      const { as, inputProps } = props;
      let el;

      switch (as) {
        case 'select': {
          const { disabled } = props;
          el = (
            <span className="input-select-wrapper">
              <Select
                {...props}
                id={inputIdRef.current}
                dataSelectorId={dataSelectorId}
                className={classNames('input', 'input-select', { disabled })}
                fluid
                value={value.formattedString}
                onFocus={handleFocus}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={disabled}
                autoComplete={inputProps.autoComplete}
              />
              {!disabled && (
                <span className="select-arrow">
                  <Icon name="chevron_down" />
                </span>
              )}
            </span>
          );
          break;
        }
        case 'textarea': {
          el = (
            <textarea
              {...props}
              id={inputIdRef.current}
              data-selector={dataSelectorId}
              rows={6}
              className="input"
              value={value.formattedString}
              onFocus={handleFocus}
              onChange={handleChange}
              onKeyPress={handleBlur}
              data-selector-type="textarea"
            />
          );
          break;
        }
        default: {
          const { type, dateInputProps } = props;
          let status = 'classicNormal';
          if (visiblyValid && displaySuccessIcon) { status = 'classicSuccess'; }
          if (visiblyInvalid) { status = 'classicError'; }

          switch (type) {
            case 'date':
            case 'datePicker': {
              el = (
                <DateInput
                  {...props}
                  {...dateInputProps}
                  id={inputIdRef.current}
                  dataSelectorId={dataSelectorId}
                  className="input"
                  defaultValue={value.formattedString}
                  onFocus={handleFocus}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  status={status}
                  errorMessage={visiblyInvalid && meta.error}
                  label={title}
                />
              );
              break;
            }
            default: {
              const inputType = ['number', 'currency', 'digit'].includes(type) ? 'tel' : type;
              const { placeholder, disabled } = props;
              el = (
                <input
                  ref={inputEl}
                  placeholder={placeholder}
                  autoComplete={inputProps.autoComplete}
                  disabled={disabled}
                  // Attribtues in inputProps take higher precedence
                  {...inputProps}
                  id={inputIdRef.current}
                  data-selector={dataSelectorId}
                  data-selector-disabled={disabled}
                  data-selector-input-is-valid={!!value.formattedString && !meta.error}
                  className="input"
                  value={value.formattedString ?? ''}
                  onFocus={handleFocus}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type={inputType}
                  aria-labelledby={`${inputIdRef.current}-label`}
                  name={name}
                  data-selector-type="input-default"
                />
              );
            }
          }
        }
      }

      return el;
    };

    const { type } = props;

    if (type === 'datePicker') { return (renderMainElement()); }

    return (/* FIELD RENNDERING PATH  */
      <div className={classNames(wrapperClasses)}>
        {(title || subtitle) && (
          <label htmlFor={inputIdRef.current} className="input-title-wrapper" id={`${inputIdRef.current}-label`}>
            {title && <strong>{title}</strong>}
            {subtitle && <Small secondary>{subtitle}</Small>}
          </label>
        )}
        {description?.length && <div className="input-description-wrapper">{description}</div>}
        {description?.position === 'pre' && (
          (description?.component && createElement(description.component, props, null))
          || description?.render(props)
        )}
        <div className="input-custom-panel-wrapper">
          {leftBoxText && (
            <div className="input-border-wrapper-left-text">
              <input className="input" type="text" disabled value={leftBoxText} />
            </div>
          )}

          <div
            className="input-border-wrapper"
            onClick={() => setIsCustomPanelShown(true)}
          >
            {leftIconName && (
              <div className="input-left-icon">
                <Icon name={leftIconName} />
              </div>
            )}
            {leftText && <div className="input-left-text">{leftText}</div>}
            {renderMainElement()}
            {rightText && <div className="input-right-text">{rightText}</div>}
            {rightIconName && (
              <div className="input-right-icon">
                <Icon name={rightIconName} />
              </div>
            )}
            {visiblyValid
              && displaySuccessIcon
              && (
                <div className="success-icon">
                  <Icon name="correct" />
                </div>
              )}
          </div>
          {customPanel && isCustomPanelShown && (
            <DialogBox
              className="custom-panel-wrapper"
              isShowArrow
              innerRef={customPanelRef}
              arrowRef={arrowRef}
            >
              <OutsideClickHandler
                display="block"
                onOutsideClick={() => {
                  onCustomPanelOutsideClick?.();
                  !overrideCustomPanelOutsideClick && setIsCustomPanelShown(false);
                }}
              >
                <div
                  className={classNames({
                    'custom-panel-padding': isCustomPanelPadded,
                  })}
                >
                  {customPanel({ hideCustomPanel: () => setIsCustomPanelShown(false) })}
                </div>
              </OutsideClickHandler>
            </DialogBox>
          )}
        </div>
        {/* display message */}
        {visiblyInvalid && ErrorComponent && <ErrorComponent {...props} error={meta.error} />}
        {
        message && displayMessageForTouchedState && !visiblyInvalid
        && <MessageComponent {...props} message={message} />
        }
        {
        message && !visiblyInvalid && !visiblyValid
        && <MessageComponent {...props} message={message} />
        }
        {description?.position === 'post' && (
          (description?.component && createElement(description.component, props, null))
          || description?.render(props)
        )}
        {/* display message */}
      </div>
    );
  }
);

const Input = sanitize({ forwardRef: true })(evolve({
  description: when(is(Object), over(lensProp('position'), defaultTo('pre'))),
  behaviors: Array.prototype.concat.bind([]),
}))(InputComponent);

InputComponent.defaultProps = {
  inputProps: {
    autoComplete: 'on',
  },
  behaviors: [],
  formatDelay: 0,
  showCustomPanel: false,
  overrideCustomPanelOutsideClick: false,
  isCustomPanelPadded: true,
  showSuccessIcon: true,
  isSuccessMessage: false,
  forcedSuccessMessage: false,
  showMessageIcon: false,
  messageIconName: 'info',
  errorIconName: 'info',
  showFeedbackOnce: 'touched',
  message: '',
  onInputFieldIsVaild: null,
  disabled: false,
  options: [],
  // eslint-disable-next-line react/prop-types
  errorComponent: ({ errorIconName, error }) => (
    <div className="input-validation-wrapper">
      <span className="input-validation-icon">
        <Icon name={errorIconName} />
      </span>
      <Small>{error}</Small>
    </div>
  ),
  messageComponent: ({
    // eslint-disable-next-line react/prop-types
    message, messageIconName, showMessageIcon, isSuccessMessage, forcedSuccessMessage,
  }) => (
    <div
      className={classNames('input-message-wrapper', {
        'success-message': isSuccessMessage,
        'forced-success-message': forcedSuccessMessage,
      })}
    >
      {showMessageIcon && (
        <span className="input-message-icon">
          <Icon name={messageIconName} />
        </span>
      )}
      <Small>{message}</Small>
    </div>
  ),
  suppressInvalidVisualResponse: false,
  displayMessageForTouchedState: false,
};

InputComponent.propTypes = {
  id: string,
  name: string,
  dataSelectorId: string,
  as: oneOf(['input', 'textarea', 'select']),
  type: oneOf(['text', 'number', 'email', 'digit', 'currency', 'tel', 'date', 'select']),
  title: string,
  required: bool,
  subtitle: string,
  description: oneOfType([
    string,
    shape({
      position: oneOf(['pre', 'post']),
      render: func,
      component: func,
    }),
  ]),
  placeholder: string,
  inputProps: shape({
    // Attributes applied to React element input. This is not exhaustive.
    autoComplete: string,
    placeholder: string,
    maxLength: number,
  }),
  dateInputProps: shape({
    yearTemplates: shape({
      one: string,
      two: string,
      three: string,
    }),
  }),
  leftIconName: string,
  leftText: string,
  leftBoxText: string,
  rightText: string,
  rightIconName: string,
  errorComponent: func,
  messageComponent: func,
  defaultValue: oneOfType([number, string, bool]),
  value: oneOfType([number, string, bool]),
  behaviors: oneOfType([
    shape({
      coerce: func,
      parse: func,
      normalize: func,
      finalize: func,
      format: func,
    }),
    arrayOf(shape({
      coerce: func,
      parse: func,
      normalize: func,
      finalize: func,
      format: func,
    })),
  ]),
  formatDelay: number,
  onFocus: func,
  onChange: func,
  onBlur: func,
  validate: func,
  showFeedbackOnce: oneOf(['init', 'dirty', 'touched']),
  customPanel: func,
  showCustomPanel: bool,
  onCustomPanelOutsideClick: func,
  overrideCustomPanelOutsideClick: bool,
  isCustomPanelPadded: bool,
  showSuccessIcon: bool,
  isSuccessMessage: bool,
  forcedSuccessMessage: bool,
  showMessageIcon: bool,
  messageIconName: string,
  errorIconName: string,
  message: string,
  onInputFieldIsVaild: func,
  disabled: bool,
  options: arrayOf(
    shape({
      label: string,
      value: oneOfType([string, bool]).isRequired,
    })
  ),
  suppressInvalidVisualResponse: bool,
  displayMessageForTouchedState: bool,
  validationText: string,
};

export default Input;
